"use client";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingButton } from "@/components/ui/loading-button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import { useScopedI18n } from "@/locales/client";
import { trpc } from "@/util/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEuCountry, isTinRequired, isValidTin } from "@talentir/shared/countries";
import { useMemo, useRef } from "react";
import type React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export function TaxData({
    onSuccess,
    footer,
    body,
}: {
    onSuccess?: () => void;
    footer?: (children: React.ReactNode) => React.ReactNode;
    body?: (children: React.ReactNode) => React.ReactNode;
}) {
    const { data: user } = useGetLoggedInUser();
    if (user == null) return;

    return <TaxDataInner user={user} onSuccess={onSuccess} footer={footer} body={body} />;
}

function TaxDataInner({
    user,
    footer,
    body,
    onSuccess,
}: {
    user: {
        countryCode: string | null;
        europeanVatNumber: string | null;
        taxpayerIdentificationNumber: string | null;
    };
    footer?: (children: React.ReactNode) => React.ReactNode;
    body?: (children: React.ReactNode) => React.ReactNode;
    onSuccess?: () => void;
}) {
    const t = useScopedI18n("vatData");
    const formRef = useRef<HTMLFormElement>(null);

    const formSchema = useMemo(
        () =>
            z.object({
                isVatPayer: z.boolean(),
                europeanVatNumber: z.string(),
                taxpayerIdentificationNumber: z
                    .string()
                    .optional()
                    .refine(
                        (value) => {
                            if (value == null || value === "") {
                                return true;
                            }
                            if (user.countryCode != null && isTinRequired(user.countryCode)) {
                                return isValidTin(value, user.countryCode);
                            }
                            return true;
                        },
                        {
                            message: "Invalid Tax Number",
                        },
                    ),
            }),
        [user.countryCode],
    );

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            isVatPayer: user.europeanVatNumber != null,
            europeanVatNumber: user.europeanVatNumber ?? "",
            taxpayerIdentificationNumber: user.taxpayerIdentificationNumber ?? "",
        },
    });

    const isVatPayer = form.watch("isVatPayer");

    const isAnEuCountry = user.countryCode != null && isEuCountry(user.countryCode);
    const tinRequired = (user.countryCode != null ? isTinRequired(user.countryCode) : false) && !isVatPayer;

    const utils = trpc.useUtils();

    const updateVatData = trpc.lambda.users.updateTaxNumber.useMutation({
        onSuccess: async () => {
            await utils.edge.users.getLoggedIn.invalidate();
            onSuccess?.();
        },
    });

    async function onSubmit(values: z.infer<typeof formSchema>) {
        if (!isVatPayer && !tinRequired) {
            updateVatData.mutate({ type: "none" });
        }

        if (isVatPayer) {
            updateVatData.mutate({ type: "vat", europeanVatNumber: values.europeanVatNumber });
        }

        if (tinRequired) {
            updateVatData.mutate({
                type: "tin",
                taxpayerIdentificationNumber: values.taxpayerIdentificationNumber ?? "",
            });
        }
    }

    if (user == null) return;

    if (!isAnEuCountry) {
        return <div>{t("notEuCountry")}</div>;
    }

    const footerInternal = footer ?? ((children) => <div className="mt-4">{children}</div>);
    const bodyInternal = body ?? ((children) => <>{children}</>);

    return (
        <Form {...form}>
            <form
                ref={formRef}
                onSubmit={(v) => {
                    void form.handleSubmit(onSubmit)(v);
                }}
                className="space-y-4"
            >
                {bodyInternal(
                    <div className="space-y-4">
                        <FormField
                            control={form.control}
                            name="isVatPayer"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("title")}</FormLabel>
                                    <FormControl>
                                        <Select
                                            {...field}
                                            value={field.value ? "true" : "false"}
                                            onValueChange={(value) => field.onChange(value === "true")}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue placeholder={t("title")} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    <SelectItem value="false">{t("options.exempt")}</SelectItem>
                                                    <SelectItem value="true">{t("options.payer")}</SelectItem>
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormDescription>{t("description")}</FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {isVatPayer && (
                            <FormField
                                control={form.control}
                                name="europeanVatNumber"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("vatNumber.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("vatNumber.placeholder")} {...field} />
                                        </FormControl>
                                        <FormDescription>{t("vatNumber.description")}</FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                        {tinRequired && (
                            <FormField
                                control={form.control}
                                name="taxpayerIdentificationNumber"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("tinNumber.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("tinNumber.placeholder")} {...field} />
                                        </FormControl>
                                        <FormDescription>{t("tinNumber.description")}</FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                    </div>,
                )}
                {footerInternal(
                    isVatPayer || tinRequired ? (
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <LoadingButton type="button" loading={updateVatData.isPending}>
                                    {t("buttons.save")}
                                </LoadingButton>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>{t("confirmDialog.title")}</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        {isVatPayer
                                            ? t("confirmDialog.descriptionVat")
                                            : t("confirmDialog.descriptionTin")}
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>{t("confirmDialog.buttons.cancel")}</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => formRef.current?.requestSubmit()}>
                                        {t("confirmDialog.buttons.confirm")}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    ) : (
                        <LoadingButton type="submit" loading={updateVatData.isPending}>
                            {t("buttons.save")}
                        </LoadingButton>
                    ),
                )}
            </form>
        </Form>
    );
}
